import axios from "axios";
import React, { useState, useEffect } from "react";
import { FaTimes, FaCheck, FaExternalLinkAlt, FaHeart, FaComment, FaEye, FaSpinner } from "react-icons/fa";
import { useToast } from "../components/common/ToastContext.tsx";

const InfluencerModal = ({ setShowInfluencer, showInfluencer, campaign }) => {
  const { showToast } = useToast();
  const [campaignInfluencers, setCampaignInfluencers] = useState<any[]>([]);
  const [postLinks, setPostLinks] = useState<any>({});
  const [showLinksModal, setShowLinksModal] = useState(false);
  const [currentInfluencer, setCurrentInfluencer] = useState<any>(null);
  const [approving, setApproving] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchingAnalytics, setFetchingAnalytics] = useState<Set<string>>(new Set());
  const [postAnalytics, setPostAnalytics] = useState<{[key: string]: any}>({});

  const rawData = localStorage.getItem('user');
  const cred = rawData ? JSON.parse(rawData) : null;
  const token = cred?.access;

  const getInfluencersTasks = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_DB_URI}/campaign-share/by_campaign/?campaign_id=${campaign?.id}`, 
        {
          headers: { "Authorization": `Bearer ${token}` }
        }
      );
      setCampaignInfluencers(response.data);
    } catch (err) {
      console.error(err);
      showToast("Failed to load influencers", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showInfluencer) {
      getInfluencersTasks();
    }
  }, [showInfluencer]);

  // NEW: Function to fetch post analytics
  const fetchPostAnalytics = async (postUrl: string) => {
    const analyticsKey = postUrl;
    
    // Don't fetch if already fetching or already have data
    if (fetchingAnalytics.has(analyticsKey) || postAnalytics[analyticsKey]) {
      return;
    }

    try {
      setFetchingAnalytics(prev => new Set(prev).add(analyticsKey));
      
      const response = await axios.get(
        `${process.env.REACT_APP_DB_URI}/users/social-info/fetch-post/?post_url=${encodeURIComponent(postUrl)}`,
        {
          headers: {
            "Authorization": `Bearer ${token}`,
            "accept": "application/json",
            "X-CSRFTOKEN": "YOUR_CSRF_TOKEN_HERE"
          }
        }
      );

      setPostAnalytics(prev => ({
        ...prev,
        [analyticsKey]: response.data
      }));

    } catch (error: any) {
      console.error('Error fetching post analytics:', error);
      // Don't show toast for analytics errors as it might be expected for some links
    } finally {
      setFetchingAnalytics(prev => {
        const newSet = new Set(prev);
        newSet.delete(analyticsKey);
        return newSet;
      });
    }
  };

  // NEW: Auto-fetch analytics when links modal opens
  useEffect(() => {
    if (showLinksModal && postLinks) {
      // Fetch analytics for all links when modal opens
      Object.values(postLinks).forEach((platformLinks: any) => {
        if (Array.isArray(platformLinks)) {
          platformLinks.forEach((post: any) => {
            if (post.link) {
              fetchPostAnalytics(post.link);
            }
          });
        }
      });
    }
  }, [showLinksModal, postLinks]);

  const approveTask = async (influencer: any) => {
    setApproving(influencer.id);
    try {
      const formData = new FormData();
      formData.append("is_verified", "true");
      formData.append("task_completed", "true");

      await axios.patch(
        `${process.env.REACT_APP_DB_URI}/campaign-share/${influencer.id}/verify/`, 
        formData,
        { 
          headers: { 
            "Authorization": `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          } 
        }
      );
      showToast("Task approved successfully!", "success");
      getInfluencersTasks();
    } catch (err) {
      console.error(err);
      showToast(err?.response?.data?.error || "Error approving task", "error");
    } finally {
      setApproving(null);
    }
  };

  // UPDATED: Now matches the exact API structure from your curl command
  const updatePostLinkStatus = async (campaignShareId: string, platform: string, link: string, status: string) => {
    try {
      // Create the updates object with platform as key and array of update objects
      const updates = {
        [platform.toLowerCase()]: [
          {
            link: link,
            status: status.toLowerCase(),
            // Add any additional properties that might be required
            additionalProp1: "string", // You may need to adjust these
            additionalProp2: "string",
            additionalProp3: "string"
          }
        ]
      };

      const payload = {
        campaign_share_id: campaignShareId,
        updates: updates
      };

      console.log('Sending payload:', JSON.stringify(payload, null, 2));

      const response = await axios.patch(
        `${process.env.REACT_APP_DB_URI}/campaign-share/update-post-link-status/`,
        payload,
        {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
            "accept": "application/json",
            "X-CSRFTOKEN": "YOUR_CSRF_TOKEN_HERE" // You might need to get this dynamically
          }
        }
      );
      return response.data;
    } catch (error: any) {
      console.error('Error updating post link status:', error.response?.data || error.message);
      throw error;
    }
  };

  // New function to approve all platforms at once
  const approveAllPlatforms = async (influencer: any) => {
    try {
      const updates = {};
      const postLinks = influencer.post_link;

      // Create updates for each platform
      Object.keys(postLinks).forEach(platform => {
        updates[platform.toLowerCase()] = postLinks[platform].map(post => ({
          link: post.link,
          status: "approved",
          additionalProp1: "string",
          additionalProp2: "string", 
          additionalProp3: "string"
        }));
      });

      const payload = {
        campaign_share_id: influencer.id,
        updates: updates
      };

      console.log('Approving all platforms:', JSON.stringify(payload, null, 2));

      const response = await axios.patch(
        `${process.env.REACT_APP_DB_URI}/campaign-share/update-post-link-status/`,
        payload,
        {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
            "accept": "application/json",
            "X-CSRFTOKEN": "YOUR_CSRF_TOKEN_HERE"
          }
        }
      );

      showToast("All platforms approved successfully!", "success");
      getInfluencersTasks();
      return response.data;
    } catch (error: any) {
      console.error('Error approving all platforms:', error);
      showToast(error.response?.data?.message || "Error approving platforms", "error");
      throw error;
    }
  };

  const SharedLinksModal = ({ influencer, postLinks, onClose }) => {
    const [localLinks, setLocalLinks] = useState(postLinks);
    const [updatingLinks, setUpdatingLinks] = useState<Set<string>>(new Set());

    // NEW: Function to manually refresh analytics for a specific post
    const refreshAnalytics = (postUrl: string) => {
      // Clear existing analytics and refetch
      setPostAnalytics(prev => {
        const newAnalytics = { ...prev };
        delete newAnalytics[postUrl];
        return newAnalytics;
      });
      fetchPostAnalytics(postUrl);
    };

    const handleLinkStatusChange = async (platform: string, link: string, newStatus: string) => {
      const updateKey = `${platform}-${link}`;
      try {
        setUpdatingLinks(prev => new Set(prev).add(updateKey));
        
        // Call the API to update link status
        await updatePostLinkStatus(influencer.id, platform, link, newStatus);

        // Update local state if API call is successful
        const updatedLinks = { ...localLinks };
        if (updatedLinks[platform]) {
          updatedLinks[platform] = updatedLinks[platform].map((post) =>
            post.link === link ? { ...post, status: newStatus } : post
          );
        }
        setLocalLinks(updatedLinks);
        
        showToast(`Link ${newStatus.toLowerCase()} successfully!`, "success");
        
        // Refresh the main influencer list to reflect changes
        getInfluencersTasks();
        
      } catch (error: any) {
        console.error("Error updating link status:", error);
        showToast(error.response?.data?.message || "Failed to update link status", "error");
      } finally {
        setUpdatingLinks(prev => {
          const newSet = new Set(prev);
          newSet.delete(updateKey);
          return newSet;
        });
      }
    };

    // Helper function to get status display text
    const getStatusDisplay = (status: string) => {
      switch (status?.toUpperCase()) {
        case 'APPROVED':
          return 'APPROVED';
        case 'REJECTED':
          return 'REJECTED';
        case 'PENDING':
          return 'PENDING';
        default:
          return status || 'PENDING';
      }
    };

    // Helper function to get status color
    const getStatusColor = (status: string) => {
      switch (status?.toUpperCase()) {
        case 'APPROVED':
          return "bg-green-100 text-green-800";
        case 'REJECTED':
          return "bg-red-100 text-red-800";
        case 'PENDING':
        default:
          return "bg-yellow-100 text-yellow-800";
      }
    };

    // NEW: Function to render analytics data for a post
    const renderPostAnalytics = (postUrl: string) => {
      const analytics = postAnalytics[postUrl];
      const isFetching = fetchingAnalytics.has(postUrl);

      if (isFetching) {
        return (
          <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
            <FaSpinner className="animate-spin" />
            Loading analytics...
          </div>
        );
      }

      if (!analytics) {
        return (
          <div className="flex items-center justify-between mt-2">
            <button
              onClick={() => fetchPostAnalytics(postUrl)}
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <FaEye />
              Fetch Analytics
            </button>
          </div>
        );
      }

      return (
        <div className="mt-3 p-2 bg-gray-50 rounded border">
          <div className="flex items-center justify-between mb-2">
            <h5 className="text-xs font-semibold text-gray-700">Post Analytics</h5>
            <button
              onClick={() => refreshAnalytics(postUrl)}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              Refresh
            </button>
          </div>
          
          <div className="grid grid-cols-2 gap-2 text-xs">
            {/* Instagram-specific metrics */}
            {analytics.likes !== undefined && (
              <div className="flex items-center gap-1 text-pink-600">
                <FaHeart className="text-xs" />
                <span>{analytics.likes.toLocaleString()} likes</span>
              </div>
            )}
            
            {analytics.comments !== undefined && (
              <div className="flex items-center gap-1 text-blue-600">
                <FaComment className="text-xs" />
                <span>{analytics.comments.toLocaleString()} comments</span>
              </div>
            )}

            {/* YouTube-specific metrics */}
            {analytics.views !== undefined && (
              <div className="flex items-center gap-1 text-red-600">
                <FaEye className="text-xs" />
                <span>{analytics.views.toLocaleString()} views</span>
              </div>
            )}

            {/* General information */}
            {analytics.owner_username && (
              <div className="col-span-2 text-xs text-gray-600">
                By: {analytics.owner_username}
              </div>
            )}

            {analytics.caption && (
              <div className="col-span-2 text-xs text-gray-600 truncate" title={analytics.caption}>
                {analytics.caption}
              </div>
            )}

            {analytics.date && (
              <div className="col-span-2 text-xs text-gray-500">
                Posted: {new Date(analytics.date).toLocaleDateString()}
              </div>
            )}

            {/* Show platform-specific identifiers */}
            {analytics.shortcode && (
              <div className="col-span-2 text-xs text-gray-500">
                Instagram Post: {analytics.shortcode}
              </div>
            )}
          </div>

          {/* Show post thumbnail if available */}
          {analytics.image_url && (
            <div className="mt-2">
              <img 
                src={analytics.image_url} 
                alt="Post thumbnail" 
                className="w-16 h-16 object-cover rounded border"
                onError={(e) => {
                  // Hide the image if it fails to load
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
        </div>
      );
    };

    // Check if all links are approved
    const allLinksApproved = () => {
      if (!localLinks) return false;
      
      let totalLinks = 0;
      let approvedLinks = 0;
      
      Object.values(localLinks).forEach((platformLinks: any) => {
        if (Array.isArray(platformLinks)) {
          platformLinks.forEach(link => {
            totalLinks++;
            if (link.status?.toUpperCase() === "APPROVED") {
              approvedLinks++;
            }
          });
        }
      });
      
      return totalLinks > 0 && totalLinks === approvedLinks;
    };

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
          <div className="flex justify-between items-center p-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white">
            <h3 className="text-lg font-bold">Shared Links - {influencer?.user_name}</h3>
            <button onClick={onClose} className="text-white hover:text-gray-200 text-xl">
              <FaTimes />
            </button>
          </div>

          <div className="p-4 overflow-y-auto max-h-[70vh]">
            {localLinks && Object.keys(localLinks).length > 0 ? (
              <div className="space-y-4">
                {/* Approve All Button */}
                {!allLinksApproved() && (
                  <div className="flex justify-center mb-4">
                    <button
                      onClick={() => approveAllPlatforms(influencer)}
                      className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition flex items-center gap-2"
                    >
                      <FaCheck />
                      Approve All Platforms
                    </button>
                  </div>
                )}

                {/* Platform Links */}
                {Object.entries(localLinks).map(([platform, platformLinks]: [string, any]) => (
                  <div key={platform} className="border border-gray-200 rounded-lg p-3">
                    <h4 className="font-semibold text-gray-800 capitalize mb-3 flex items-center gap-2">
                      {platform}
                      <span className={`px-2 py-1 text-xs rounded ${getStatusColor(
                        platformLinks.every((link: any) => link.status?.toUpperCase() === "APPROVED") 
                          ? "approved" 
                          : "pending"
                      )}`}>
                        {platformLinks.every((link: any) => link.status?.toUpperCase() === "APPROVED") 
                          ? "All Approved" 
                          : "Pending Review"}
                      </span>
                    </h4>
                    
                    <div className="space-y-4">
                      {platformLinks.map((post: any, index: number) => {
                        const updateKey = `${platform}-${post.link}`;
                        const isUpdating = updatingLinks.has(updateKey);
                        
                        return (
                          <div key={index} className="bg-gray-50 p-3 rounded border">
                            <div className="flex items-start justify-between mb-2">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(post.status)}`}>
                                {getStatusDisplay(post.status)}
                              </span>
                            </div>
                            
                            <a
                              href={post.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1 break-all mb-3"
                            >
                              {post.link}
                              <FaExternalLinkAlt className="text-xs flex-shrink-0" />
                            </a>

                            {/* NEW: Render post analytics */}
                            {renderPostAnalytics(post.link)}

                            {post.status?.toUpperCase() !== "APPROVED" && (
                              <div className="flex gap-2 mt-3">
                                <button
                                  className="flex-1 text-xs px-3 py-2 bg-green-100 text-green-700 rounded hover:bg-green-200 transition disabled:opacity-50 flex items-center justify-center gap-1"
                                  onClick={() => handleLinkStatusChange(platform, post.link, "APPROVED")}
                                  disabled={isUpdating}
                                >
                                  {isUpdating ? (
                                    <>
                                      <FaSpinner className="animate-spin" />
                                      Updating...
                                    </>
                                  ) : (
                                    'Approve'
                                  )}
                                </button>
                                {post.status?.toUpperCase() !== "REJECTED" && (
                                  <button
                                    className="flex-1 text-xs px-3 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200 transition disabled:opacity-50 flex items-center justify-center gap-1"
                                    onClick={() => handleLinkStatusChange(platform, post.link, "REJECTED")}
                                    disabled={isUpdating}
                                  >
                                    {isUpdating ? (
                                      <>
                                        <FaSpinner className="animate-spin" />
                                        Updating...
                                      </>
                                    ) : (
                                      'Reject'
                                    )}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaExternalLinkAlt className="text-gray-400 text-2xl" />
                </div>
                <p className="text-gray-500">No links shared yet</p>
              </div>
            )}
          </div>

          <div className="p-4 border-t border-gray-200 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {influencer?.is_verified ? (
                <span className="text-green-600 font-medium">✓ Campaign Verified</span>
              ) : (
                <span className="text-yellow-600">Pending Verification</span>
              )}
            </div>
            <button 
              onClick={onClose} 
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex justify-between items-center p-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white">
          <h2 className="text-xl font-bold">Influencers in Campaign</h2>
          <button onClick={() => setShowInfluencer(false)} className="text-white hover:text-gray-200 text-xl">
            <FaTimes />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8">
            <div className="border-t-4 border-teal-600 rounded-full w-12 h-12 animate-spin mb-4"></div>
            <p className="text-teal-700">Loading influencers...</p>
          </div>
        ) : campaignInfluencers.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
              <FaCheck className="text-gray-400 text-2xl" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No influencers yet</h3>
            <p className="text-gray-500 max-w-md">
              This campaign doesn't have any influencers assigned yet.
            </p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {campaignInfluencers.map((influencer) => {
                const linksArray = influencer.post_link ? Object.values(influencer.post_link).flat() : [];
                const totalLinks = linksArray.length;
                const approvedLinks = linksArray.filter(link => link.status?.toUpperCase() === "APPROVED").length;
                const pendingLinks = linksArray.filter(link => 
                  !link.status || link.status?.toUpperCase() === "PENDING"
                ).length;

                return (
                  <div key={influencer.id} className="border rounded-xl p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-3">
                      {influencer?.user_details?.profile_picture ? (
                        <img
                          src={`${process.env.REACT_APP_PF}${influencer.user_details.profile_picture}`}
                          alt={influencer.user_name}
                          className="w-12 h-12 rounded-full object-cover border-2 border-white shadow"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/default-avatar.jpg';
                          }}
                        />
                      ) : (
                        <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full text-lg font-bold text-teal-700">
                          {influencer.user_name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-800">{influencer.user_name}</h3>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {totalLinks > 0 && (
                            <>
                              <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                                {approvedLinks} approved
                              </span>
                              {pendingLinks > 0 && (
                                <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                                  {pendingLinks} pending
                                </span>
                              )}
                            </>
                          )}
                          {influencer.is_verified && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                              ✓ Verified
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2 mt-4">
                      {!influencer.task_completed && totalLinks > 0 && (
                        <button
                          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${
                            approving === influencer.id
                              ? "bg-blue-300 cursor-not-allowed"
                              : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                          }`}
                          onClick={() => approveTask(influencer)}
                          disabled={approving === influencer.id}
                        >
                          {approving === influencer.id ? (
                            <>
                              <FaSpinner className="animate-spin" />
                              Approving...
                            </>
                          ) : (
                            <>
                              <FaCheck /> Approve All
                            </>
                          )}
                        </button>
                      )}

                      <button
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-teal-100 text-teal-700 rounded-lg text-sm font-medium hover:bg-teal-200 transition"
                        onClick={() => {
                          setPostLinks(influencer.post_link || {});
                          setCurrentInfluencer(influencer);
                          setShowLinksModal(true);
                        }}
                      >
                        <FaExternalLinkAlt className="text-xs" />
                        View Links ({totalLinks})
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {showLinksModal && (
        <SharedLinksModal
          influencer={currentInfluencer}
          postLinks={postLinks}
          onClose={() => setShowLinksModal(false)}
        />
      )}
    </div>
  );
};

export default InfluencerModal;